// src/components/credit/CreditDocumentPreview.tsx
import React from 'react';
import { FileText, ShieldCheck, UserCheck } from 'lucide-react';

interface CreditDocumentPreviewProps {
  app: {
    reference_no: string;
    company_name: string;
    salesperson_name: string;
    sales_region: string;
    currency: string;
  };
  answers: {
    business_type: string;
    established_since: string;
    customer_emails: string[];
    customer_phones: string[];
    description_and_market_position: string;
    relationship_duration: string;
    additional_customer_info: string;
    requested_credit_limit: number | '';
    requested_credit_terms: string;
    custom_credit_terms: string;
    credit_type: string;
    credit_type_other: string;
    expected_purchase_volume: string;
    business_justification: string;
  };
  signatory?: {
    name: string;
    designation: string;
    company: string;
    email: string;
    phone: string;
    signature_data?: string;
    signature_method?: string;
  };
}

export const CreditDocumentPreview: React.FC<CreditDocumentPreviewProps> = ({
  app,
  answers,
  signatory,
}) => {
  const formattedLimit = answers.requested_credit_limit
    ? `${app.currency} ${Number(answers.requested_credit_limit).toLocaleString('en-IN')}`
    : '—';

  const terms =
    answers.requested_credit_terms === 'Other' && answers.custom_credit_terms
      ? answers.custom_credit_terms
      : answers.requested_credit_terms;

  const creditType =
    answers.credit_type === 'Others' && answers.credit_type_other
      ? `Others: ${answers.credit_type_other}`
      : answers.credit_type;

  return (
    <div className="bg-white border border-slate-300 rounded-lg shadow-sm p-6 sm:p-8 max-w-4xl mx-auto space-y-6 text-slate-800 font-sans print:border-none print:shadow-none">
      {/* Header Banner */}
      <div className="border-b-2 border-[#0f5a2e] pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-black tracking-tight text-[#0f5a2e]">GEOBIT GROUP</span>
          </div>
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900 mt-1">
            Customer Credit Intake Form
          </h2>
        </div>
        <div className="text-right sm:text-right">
          <div className="text-xs font-bold text-[#0f5a2e]">Ref No: GIND/CIF/01/2026</div>
          <div className="text-xs text-slate-500 font-mono mt-0.5">App Ref: {app.reference_no}</div>
        </div>
      </div>

      {/* 1. APPLICATION SUMMARY */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          1. Application Summary
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 border border-slate-200 text-xs divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-3 space-y-2">
            <div>
              <span className="text-slate-500 block font-medium">Customer / Company Name:</span>
              <span className="font-semibold text-slate-900 text-sm">{app.company_name}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Business Type / Industry:</span>
              <span className="text-slate-900">{answers.business_type || '—'}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Established Since:</span>
              <span className="text-slate-900">{answers.established_since || '—'}</span>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div>
              <span className="text-slate-500 block font-medium">Application Date:</span>
              <span className="text-slate-900">{new Date().toISOString().split('T')[0]}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Salesperson Name:</span>
              <span className="text-slate-900 font-semibold">{app.salesperson_name}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Sales Region / Territory:</span>
              <span className="text-slate-900">{app.sales_region}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. CUSTOMER CONTACT INFORMATION */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          2. Customer Contact Information
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 border border-slate-200 text-xs divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-3">
            <span className="text-slate-500 font-medium block mb-1.5">Customer Email IDs (Minimum 3):</span>
            <ol className="list-decimal list-inside space-y-1 font-mono text-slate-800">
              {answers.customer_emails.filter(Boolean).map((e, idx) => (
                <li key={idx}>{e}</li>
              ))}
            </ol>
          </div>
          <div className="p-3">
            <span className="text-slate-500 font-medium block mb-1.5">Customer Contact Numbers (Minimum 3):</span>
            <ol className="list-decimal list-inside space-y-1 font-mono text-slate-800">
              {answers.customer_phones.filter(Boolean).map((p, idx) => (
                <li key={idx}>{p}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {/* 3. CUSTOMER BACKGROUND & RELATIONSHIP OVERVIEW */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          3. Customer Background & Relationship Overview
        </div>
        <div className="border border-slate-200 text-xs divide-y divide-slate-200">
          <div className="p-3">
            <span className="text-slate-500 font-medium block mb-1">1. Brief Description of Customer & Market Position:</span>
            <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">
              {answers.description_and_market_position || 'Not provided'}
            </p>
          </div>
          <div className="p-3">
            <span className="text-slate-500 font-medium block mb-1">2. How long has customer been dealing with Geobit Group?</span>
            <span className="font-semibold text-slate-900">{answers.relationship_duration}</span>
          </div>
          <div className="p-3 bg-slate-50">
            <span className="text-[#0f5a2e] font-semibold block mb-1">
              3. Any Credit Risk Concerns / Mitigating Factors [Internal Assessment]:
            </span>
            <span className="text-slate-500 italic">Pending Internal Assessment & Review</span>
          </div>
        </div>
      </div>

      {/* 4. CREDIT DETAILS & REQUEST */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          4. Credit Details & Request
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 border border-slate-200 text-xs divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-3 space-y-2">
            <div>
              <span className="text-slate-500 block font-medium">Requested Credit Limit:</span>
              <span className="font-bold text-sm text-[#0f5a2e]">{formattedLimit}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Requested Credit Terms:</span>
              <span className="text-slate-900 font-semibold">{terms}</span>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <div>
              <span className="text-slate-500 block font-medium">Type of Credit Requested:</span>
              <span className="text-slate-900">{creditType}</span>
            </div>
            <div>
              <span className="text-slate-500 block font-medium">Expected Monthly / Annual Purchase Volume:</span>
              <span className="text-slate-900">{answers.expected_purchase_volume || '—'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 5. RISK & CREDIT ASSESSMENT */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          5. Risk & Credit Assessment
        </div>
        <div className="border border-slate-200 text-xs divide-y divide-slate-200">
          <div className="p-3">
            <span className="text-slate-500 font-medium block mb-1">Reason for Credit Request (Business Justification):</span>
            <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">
              {answers.business_justification || 'Standard commercial facility request.'}
            </p>
          </div>
          <div className="p-3 bg-slate-50">
            <span className="text-[#0f5a2e] font-semibold block mb-1">Customer's Payment History (if applicable) [Internal Assessment]:</span>
            <span className="text-slate-500 italic">Pending Internal Assessment</span>
          </div>
          <div className="p-3 bg-slate-50">
            <span className="text-[#0f5a2e] font-semibold block mb-1">Salesperson's Recommendation [Internal Assessment]:</span>
            <span className="text-slate-500 italic">Pending Sales Management Sign-off</span>
          </div>
        </div>
      </div>

      {/* 6. SIGN-OFF & AUTHORIZATION */}
      <div className="space-y-2">
        <div className="bg-[#0f5a2e] text-white px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded">
          6. Sign-off & Authorization
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 border border-slate-200 text-xs divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
          <div className="p-3 space-y-2">
            <div className="font-semibold text-[#0f5a2e]">Customer Authorized Signatory</div>
            {signatory ? (
              <div className="space-y-1">
                <div>Name: <strong>{signatory.name}</strong></div>
                <div>Designation: {signatory.designation}</div>
                <div>Company: {signatory.company}</div>
                <div>Email: {signatory.email}</div>
                <div className="pt-2">
                  {signatory.signature_method === 'draw' && signatory.signature_data?.startsWith('data:image') ? (
                    <img src={signatory.signature_data} alt="Signature" className="h-10 border border-slate-200 p-1 bg-white" />
                  ) : (
                    <div className="font-serif italic text-base font-bold text-[#0f5a2e]">
                      {signatory.name}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span className="text-slate-400 italic">To be completed in Step 3</span>
            )}
          </div>
          <div className="p-3 space-y-2 bg-slate-50">
            <div className="font-semibold text-slate-700">Internal Sales Sign-off</div>
            <div>Salesperson Name: {app.salesperson_name}</div>
            <div>Sales Region: {app.sales_region}</div>
            <div className="pt-4 text-slate-400">Signature: ______________________</div>
            <div className="text-slate-400">Date: __________________</div>
          </div>
        </div>
      </div>
    </div>
  );
};
